import type { EventCode } from "../../contracts/values";
import { isMessageEnabled } from "../../contracts/message-enabled";
import { hasChatMessage, templateVariableNames, type ModuleDiagnostic, type ModuleEvent, type ModuleLanguage, type ModuleResult } from "../contract";
import type { AdsSettings } from "./contracts";
import { decideAdBreak, localizeAdDurationGrammar, renderAdBreakText } from "./domain";
import { adsChatLanguage } from "./contracts/language";

const diagnoseDetail = (
  event: ReturnType<typeof decideAdBreak>,
): Record<string, string | number | boolean | null> => {
  if (event.kind === "skip") {
    return {
      reason: event.reason,
      duration: event.durationSeconds,
      automatic: event.automatic,
    };
  }
  return {
    duration: event.event.durationSeconds,
    automatic: event.event.automatic,
    startedAt: event.event.startedAt,
    endsAt: event.event.endsAt,
    triggerLogin: event.event.triggerLogin,
  };
};

const diagnosticCode = (event: ReturnType<typeof decideAdBreak>): EventCode =>
  event.kind === "announce" ? "ads.announcement" : "ads.skipped";

export function processAdBreak(event: ModuleEvent<AdsSettings>): ModuleResult;
export function processAdBreak(
  event: ModuleEvent<AdsSettings>,
  render: (text: string, values: Readonly<Record<string, string | number>>) => Promise<{ text: string; diagnostics: readonly ModuleDiagnostic[] }>,
  language: ModuleLanguage,
): Promise<ModuleResult>;
export function processAdBreak(
  event: ModuleEvent<AdsSettings>,
  render?: (text: string, values: Readonly<Record<string, string | number>>) => Promise<{ text: string; diagnostics: readonly ModuleDiagnostic[] }>,
  language: ModuleLanguage = "de",
): ModuleResult | Promise<ModuleResult> {
  const decision = decideAdBreak(event.payload);
  if (decision.kind === "skip") {
    return {
      actions: [],
      diagnostics: [{ code: diagnosticCode(decision), detail: diagnoseDetail(decision) }],
    };
  }

  const template = decision.event.automatic ? event.settings.automatic : event.settings.manual;
  const messageKey = decision.event.automatic ? "automatic" : "manual";
  const enabledKey = decision.event.automatic ? "automaticEnabled" : "manualEnabled";
  const target = (decision.event.automatic ? event.settings.automaticTarget : event.settings.manualTarget) ?? "source_only";
  const makeResult = (text: string, templateDiagnostics: readonly ModuleDiagnostic[] = []): ModuleResult => ({
    actions: hasChatMessage(text) ? [{ kind: "chat", text, target }] : [],
    diagnostics: [...templateDiagnostics, { code: diagnosticCode(decision), detail: diagnoseDetail(decision) }],
  });
  if (!isMessageEnabled(event.settings, messageKey, enabledKey) || !hasChatMessage(template)) return makeResult("");
  if (render !== undefined) {
    return render(template, { "ads.duration": decision.event.durationSeconds }).then(({ text, diagnostics }) => {
      const hasDuration = templateVariableNames(template.trim()).includes("ads.duration");
      const duration = decision.event.durationSeconds;
      const suffix = duration === 1 ? adsChatLanguage[language].second : adsChatLanguage[language].seconds;
      const completed = localizeAdDurationGrammar(hasDuration ? text : `${text} (${String(duration)} ${suffix})`, duration, language);
      return makeResult(completed, diagnostics);
    });
  }
  return makeResult(renderAdBreakText(template, decision.event.durationSeconds, language));
}
